import {Button} from '@wandb/weave/components/Button/Button';
import {get} from 'lodash';
import React, {useCallback} from 'react';

import {ClassLabelNode} from '../../../../../../util/media';
import {MaskControl} from '../../../../../MediaCard';
import {
  buildMaskControl,
  getMaskControl as getDefaultedMaskControl,
} from '../../../../../PanelMediaBrowser/utils/maskControlUtils';
import {useMediaSettingsDrawerContext} from '../../MediaSettingsDrawerContextProvider';
import {MaskClasses} from './MaskClasses';

type MasksForMediaKeyProps = {
  mediaKey: string;
  maskSearchRegex: RegExp | null;
};

export const MasksForMediaKey = ({
  mediaKey,
  maskSearchRegex,
}: MasksForMediaKeyProps) => {
  const {allMasks, updateConfig, config} = useMediaSettingsDrawerContext();

  const toggleAllMasksUnder = (
    maskKey?: string,
    classID?: string,
    relevantMasks?: {[key: string]: ClassLabelNode}
  ): void => {
    const localMaskKey = maskKey ?? 'all';
    const localClassID = classID ?? 'all';
    const localRelevantMasks = relevantMasks ?? allMasks[mediaKey];

    const pathsToInclude: Array<Array<string>> = [
      ['toggles', mediaKey, localMaskKey, localClassID],
    ];
    Object.keys(localRelevantMasks).forEach(childMaskKey => {
      if (localMaskKey === 'all' || childMaskKey === localMaskKey) {
        if (localClassID === 'all') {
          pathsToInclude.push([
            'toggles',
            mediaKey,
            childMaskKey,
            localClassID,
          ]);
        }
        Object.keys(localRelevantMasks[childMaskKey].value).forEach(
          childClassID => {
            if (localClassID === 'all' || childClassID === localClassID) {
              pathsToInclude.push([
                'toggles',
                mediaKey,
                childMaskKey,
                childClassID,
              ]);
            }
          }
        );
      }
    });

    const currentMaskConfig = config.segmentationMaskConfig ?? {};
    const maskControl = getDefaultedMaskControl(currentMaskConfig, [
      'toggles',
      mediaKey,
      localMaskKey,
      localClassID,
    ]);
    const disabled = maskControl.disabled;

    const pathsToUpdate = pathsToInclude.filter(path => {
      const maskControl = getDefaultedMaskControl(currentMaskConfig, path);
      return maskControl.disabled === disabled;
    });
    if (pathsToUpdate.length > 0) {
      let newConfig = {...currentMaskConfig};
      pathsToUpdate.forEach(configPath => {
        const maskControl: MaskControl = getDefaultedMaskControl(
          currentMaskConfig,
          configPath
        );
        newConfig = buildMaskControl(
          {...maskControl, disabled: !maskControl.disabled},
          newConfig,
          configPath
        );
      });
      updateConfig({segmentationMaskConfig: newConfig});
    }
  };

  const relevantMasks = allMasks[mediaKey];

  const allMasksConfigPath = ['toggles', mediaKey, 'all', 'all'];
  const getMaskControl = useCallback(
    (configPath: Array<string>): MaskControl | undefined => {
      return get(config.segmentationMaskConfig ?? {}, configPath);
    },
    [config.segmentationMaskConfig]
  );
  const allMasksControl = getMaskControl(allMasksConfigPath);

  return (
    <div className="flex flex-col">
      <div className="ml-8 flex items-center">
        <Button
          data-test={`visibility-toggle-${mediaKey}-all`}
          variant="ghost"
          icon={!allMasksControl?.disabled ? 'visible' : 'not-visible'}
          onClick={() => toggleAllMasksUnder('all', 'all')}
        />
        <div className="ml-4 font-semibold">All Masks</div>
      </div>
      {Object.keys(relevantMasks).map(maskKey => (
        <MaskClasses
          getMaskControl={getMaskControl}
          maskSearchRegex={maskSearchRegex}
          maskKey={maskKey}
          mediaKey={mediaKey}
          key={`${mediaKey}-${maskKey}`}
        />
      ))}
    </div>
  );
};
